import { Component, EventEmitter, Injectable, OnInit } from '@angular/core';
import { ModalController, Platform } from '@ionic/angular';
import { BehaviorSubject, Observable, Subject, debounceTime, delay, distinctUntilChanged, filter, fromEvent, map, merge, take, takeUntil, takeWhile, tap, timer } from 'rxjs';
@Injectable({
  providedIn: 'root'
})
export class OrientationService {
  public currentOrientation$: Observable<OrientationType>;
  public lockedOrientation$ = new BehaviorSubject<OrientationType | null>(null);
  public lockIsReady$ = new BehaviorSubject<boolean>(false);
  public hasScreenOrientationEvent = new BehaviorSubject(false);
  public lockSuccess = new BehaviorSubject(false);
  private updateEvent = new EventEmitter();
  private modalActive = false;
  public modalActive$ = new BehaviorSubject(false);

  constructor(
    private modalController: ModalController,
    private platform: Platform
  ) {
    const events = [
      this.updateEvent,
      fromEvent(document, 'visibilitychange'),
      fromEvent(document, 'fullscreenchange'),
      fromEvent(window, 'resize')
    ]

    if (screen.orientation) {    
      events.push(fromEvent(screen.orientation, 'change'));
      this.hasScreenOrientationEvent.next(true);
    }

    this.currentOrientation$ = merge(...events).pipe(
      debounceTime(500),
      map(() => this.checkOrientation())
    );

  }

  /** 
   * Calcula la relación de aspecto y devuelve OrientationType  
  */
  public checkOrientation(): OrientationType {
    // screen.orientation.type funciona, pero no en dispositivos más viejos
    // Por esto utilizaremos window.outerWidth y window.outerHeight
    const aspectRatio = window.outerWidth / window.outerHeight;
    return aspectRatio >= 1 ? 'landscape-primary' : 'portrait-primary';
  }

  /**
   * Fuerza al usuario a rotar la pantalla para que coincida con la orientación deseada
   * mostrando un modal cada vez que la orientación no coincide
   * y haciendo uso de la API de Orientación de Pantalla y Pantalla Completa
   * @param orientation - La orientación deseada
   */
  public lockScreen(orientation: OrientationType): void {

    this.lockedOrientation$.next(orientation);
    this.currentOrientation$
      .pipe(
        takeWhile(() => !!this.lockedOrientation$.value),
        distinctUntilChanged(),
      )
      .subscribe({
        next: value => {
          // this.togglefullScreen();
          if (this.platform.is('desktop')) {
            this.lockIsReady$.next(true);
          } else if (!this.modalActive) {
            if (value === this.lockedOrientation$.value) {
              this.lockIsReady$.next(true);
            } else {
              this.lockIsReady$.next(false);
              this.modalActive = true; 
              this.modalActive$.next(true);
              this.showOrientationModal().then(() => {
                this.modalActive = false;
                this.modalActive$.next(false);
                this.lockIsReady$.next(true);
                // this.togglefullScreen();
              })
            }
          }
        },
        complete: () => {
          this.lockIsReady$.next(false);
          // if (this.isFullscreenActive()) {
          //   document.exitFullscreen().catch(err => console.warn(err));
          // }
        }
      });
    this.updateEvent.emit();
  }

  /**
   * Desbloquea la pantalla si ha sido bloqueada
  */
  public unlock(): void {
    this.lockedOrientation$.next(null);
    this.updateEvent.emit();
  }

  /**
   * Muestra un modal que solicita al usuario que rote el dispositivo
  */
  private async showOrientationModal(): Promise<boolean> {
    console.log('Showing orientation modal');
    const modal = await this.modalController.create({
      component: OrientationModalComponent,
      animated: false,
      cssClass:  "orientationModal"
    })

    await modal.present();

    return new Promise<boolean>(resolve => {
      modal
        .onDidDismiss()
        .then(() => resolve(true))
        .catch(() => resolve(false));
    })
  }

}

@Component({
  selector: 'app-orientation-modal',
  templateUrl: './orientation.component.html',
  styleUrls: ['./orientation.component.scss'],
})
export class OrientationModalComponent implements OnInit {
  constructor(
    public orientation: OrientationService,
    private modalController: ModalController
  ) {}

  public correctOrientation = false;

  ngOnInit(): void {
    this.orientation.currentOrientation$
    .pipe(
      filter(value => this.isCorrect(value)),
      tap(() => this.correctOrientation = true),
      take(1),
      delay(2000)
    )
    .subscribe(() => {
      this.modalController.dismiss();
    });
  }

  orientationString(): string {
    return this.orientation.lockedOrientation$.value!.split("-")[0];
  }

  isCorrect(value: OrientationType): boolean {
    return value === this.orientation.lockedOrientation$.value;
  }
}
